import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { ActiveStatusBadge } from '../../components/ui/ActiveStatusBadge';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { SortOrderControls } from '../../components/ui/SortOrderControls';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { useSortOrderReorder } from '../../hooks/useSortOrderReorder';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import {
  JobPositionAttributionKind,
  JobPositionAttributionKindUpsertPayload,
  createJobAttributionKind,
  deleteJobAttributionKind,
  listJobAttributionKinds,
  updateJobAttributionKind,
} from '../../services/jobAttributionKindsApi';
import { nextSortOrder } from '../../utils/sortOrderList';

type FormState = Omit<JobPositionAttributionKindUpsertPayload, 'sortOrder'>;

const ATTRIBUTION_KIND_DESCRIPTION_MAX_LENGTH = 4000;

const emptyForm: FormState = {
  name: '',
  description: '',
  isActive: true,
};

function buildKindPayload(
  item: JobPositionAttributionKind,
  sortOrder: number,
): JobPositionAttributionKindUpsertPayload {
  return {
    name: item.name,
    description: item.description,
    sortOrder,
    isActive: item.isActive,
  };
}

export function JobAttributionKindsPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('people.job-positions');
  const [items, setItems] = useState<JobPositionAttributionKind[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } = useCollapsibleItemForm();

  async function loadData(): Promise<void> {
    setLoading(true);
    try {
      setItems(await listJobAttributionKinds(false));
    } catch {
      toast.error('Falha ao carregar tipos de atribuição.');
    } finally {
      setLoading(false);
    }
  }

  const { sortedItems, moving, move } = useSortOrderReorder({
    items,
    canReorder: canWrite,
    updateItem: async (item, sortOrder) => {
      await updateJobAttributionKind(item.id, buildKindPayload(item, sortOrder));
    },
    onReordered: () => loadData(),
  });

  useEffect(() => {
    void loadData();
  }, []);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  function startEdit(item: JobPositionAttributionKind): void {
    expandForm();
    setEditingId(item.id);
    setForm({
      name: item.name,
      description: item.description,
      isActive: item.isActive,
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const editingItem = editingId ? items.find((item) => item.id === editingId) : null;
    const payload: JobPositionAttributionKindUpsertPayload = {
      ...form,
      sortOrder: editingItem?.sortOrder ?? nextSortOrder(items),
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateJobAttributionKind(editingId, payload);
        toast.success('Tipo atualizado.');
        collapseForm(resetForm);
      } else {
        await createJobAttributionKind(payload);
        toast.success('Tipo criado.');
        handleCreateSuccess(resetForm);
      }
      await loadData();
    } catch {
      toast.error(editingId ? 'Falha ao atualizar tipo.' : 'Falha ao criar tipo.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: JobPositionAttributionKind): Promise<void> {
    if (item.isSystem) {
      toast.error('Tipos padrão do sistema não podem ser excluídos.');
      return;
    }

    const ok = await confirm({
      title: 'Excluir tipo',
      message: `Excluir o tipo "${item.name}"?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!ok) return;

    try {
      await deleteJobAttributionKind(item.id);
      toast.success('Tipo excluído.');
      await loadData();
    } catch {
      toast.error('Não foi possível excluir o tipo.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Tipos de atribuição do cargo"
        description="Catálogo do que compete ao cargo por categoria. Tipos de sistema não podem ser excluídos."
        actions={(
          <Link to="/people/job-positions" className="erp-btn-secondary text-sm">
            Voltar para cargos
          </Link>
        )}
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar tipo' : 'Novo tipo'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="space-y-3">
            <div className="erp-form-grid md:grid-cols-2">
              <input
                className="erp-input-compact"
                placeholder="Nome"
                value={form.name}
                required
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
              <textarea
                className="erp-input-compact md:col-span-2 min-h-[88px]"
                placeholder="Descrição (explica o tipo)"
                value={form.description}
                maxLength={ATTRIBUTION_KIND_DESCRIPTION_MAX_LENGTH}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
              />
              <span className="text-xs text-secondary-500 md:col-span-2">
                {(form.description ?? '').length} / {ATTRIBUTION_KIND_DESCRIPTION_MAX_LENGTH} caracteres
              </span>
              <label className="erp-label-inline">
                <input
                  type="checkbox"
                  checked={form.isActive ?? true}
                  onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
                />
                Ativo
              </label>
            </div>
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
            >
              <button type="submit" className="erp-btn-primary" disabled={saving}>
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <section className="erp-card">
        <div className="erp-card-body">
          {loading ? (
            <p className="text-sm text-secondary-500">Carregando...</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="erp-table">
                <thead>
                  <tr>
                    {canWrite ? <th className="w-10" /> : null}
                    <th>Nome</th>
                    <th>Sistema</th>
                    <th>Ativo</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((item, index) => (
                    <tr key={item.id}>
                      {canWrite ? (
                        <td>
                          <SortOrderControls
                            canMoveUp={index > 0}
                            canMoveDown={index < sortedItems.length - 1}
                            disabled={moving}
                            onMoveUp={() => void move(index, 'up')}
                            onMoveDown={() => void move(index, 'down')}
                          />
                        </td>
                      ) : null}
                      <td>
                        <div className="font-medium text-secondary-900">{item.name}</div>
                        <div className="text-xs text-secondary-500 line-clamp-2">{item.description}</div>
                      </td>
                      <td>{item.isSystem ? 'Sim' : 'Não'}</td>
                      <td>
                        <ActiveStatusBadge
                          active={item.isActive}
                          activeLabel="Sim"
                          inactiveLabel="Não"
                        />
                      </td>
                      <td className="text-right whitespace-nowrap">
                        {canWrite ? (
                          <>
                            <EditIconButton onClick={() => startEdit(item)} />
                            {!item.isSystem ? (
                              <DeleteIconButton onClick={() => void handleDelete(item)} />
                            ) : null}
                          </>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
}
